import { useState, useEffect, useContext } from "react";

import StaffingDialog from "../../components/StaffingDialog/StaffingDialog";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { Button, ButtonGroup, IconButton, Menu, MenuItem, Checkbox, ListItemText } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

const Calendar = () => {
  const { userData } = useContext(EventContext);
  const [events, setEvents] = useState([]);
  const [staffing, setStaffing] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [staffingOpen, setStaffingOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState("all");

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStaffFilters, setSelectedStaffFilters] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      await getEvents();
      await getStaffing();
      if (
        userData.permissions.canManageStaff === true ||
        userData.role === "superadmin"
      ) {
        getStaff();
      }
    };
    if (userData) {
      loadData();
    }
    const onResize = () => setIsMobile(window.innerWidth < 600);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [userData]);

  const getEvents = async () => {
    const result = await request("staff/events");
    if (result.success) {
      setEvents(result.data);
    }
  };

  const getStaffing = async () => {
    const result = await request("staff");
    if (result.success) {
      setStaffing(result.data);
    }
  };

  const getStaff = async () => {
    const result = await request("staff/org");
    if (result.success) {
      setStaff(result.data);
    }
  };

  const handleFilterClick = (event) => setAnchorEl(event.currentTarget);
  const handleFilterClose = () => setAnchorEl(null);
  const toggleStaffFilter = (userId) => {
    setSelectedStaffFilters((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredEvents =
    viewMode === "my"
      ? events.filter((e) =>
          staffing.some((s) => s.eventId === e._id && s.userId === userData._id)
        )
      : selectedStaffFilters.length > 0
      ? events.filter((e) =>
          staffing.some(
            (s) =>
              s.eventId === e._id &&
              selectedStaffFilters.includes(s.userId)
          )
        )
      : events;

  return (
    <div style={{ padding: "20px" }}>
      <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
        <Button
          variant={viewMode === "all" ? "contained" : "outlined"}
          onClick={() => setViewMode("all")}
        >
          All Events
        </Button>
        <Button
          variant={viewMode === "my" ? "contained" : "outlined"}
          onClick={() => setViewMode("my")}
        >
          My Events
        </Button>
      </ButtonGroup>
      {(userData?.permissions?.canManageStaff || userData?.role === "superadmin") && (
        <>
          <IconButton onClick={handleFilterClick} sx={{ ml: 1 }}>
            <FilterListIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleFilterClose}>
            {staff.map((member) => (
              <MenuItem key={member._id} onClick={() => toggleStaffFilter(member._id)}>
                <Checkbox checked={selectedStaffFilters.includes(member._id)} />
                <ListItemText primary={`${member.firstName} ${member.lastName}`} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        dayMaxEventRows={isMobile ? 4 : false}
        moreLinkText={"+ more"}
        events={filteredEvents.map((e) => {
          const hasStaff = staffing.some((s) => s.eventId === e._id);
          return {
            id: e._id,
            title: e.name,
            date: e.date,
            ...(isMobile
              ? {
                  // On mobile keep backgrounds neutral; use border cue only
                  borderColor: hasStaff ? "#1E90FF" : "#FF6B6B",
                }
              : {
                  backgroundColor: hasStaff ? "#1E90FF" : "#FF6B6B",
                  borderColor: hasStaff ? "#1E90FF" : "#FF6B6B",
                }),
          };
        })}
        eventContent={(arg) => {
          const hasStaff = staffing.some((s) => s.eventId === arg.event.id);
          const title = arg.event.title;
          return (
            <div
              style={{
                whiteSpace: "normal",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {!hasStaff && (
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#FF6B6B",
                      flex: "0 0 auto",
                    }}
                  />
                )}
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.85em" : "1em",
                    display: "-webkit-box",
                    WebkitLineClamp: isMobile ? 2 : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {title}
                </span>
              </div>
              {!hasStaff && !isMobile && (
                <div style={{ color: "#FF6B6B", fontSize: "0.8em" }}>
                  No staff assigned
                </div>
              )}
            </div>
          );
        }}
        eventClick={(arg) => {
         if (userData?.permissions?.canManageStaff || userData?.role === "superadmin"){
             setSelectedEvent(arg.event.id);
             setStaffingOpen(true);
         }
        }}
        height="90vh"
      />
      <StaffingDialog
        open={staffingOpen}
        close={() => {
          setStaffingOpen(false);
          getStaffing();
        }}
        eventId={selectedEvent}
      />
    </div>
  );
};

export default Calendar;
